"use client";

import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

export interface PrintProps {
  eco: boolean;
}

interface PrintContextType {
  handlePrint: (settings: PrintProps) => void;
  isPrinting: boolean;
  printSettings: PrintProps | null;
}

const PrintContext = createContext<PrintContextType | undefined>(undefined);

export function PrintProvider({ children }: { children: ReactNode }) {
  const [printSettings, setPrintSettings] = useState<PrintProps | null>(null);

  // Trigger print when settings change
  useEffect(() => {
    if (printSettings !== null) {
      // Schedule print on next tick to ensure render completes first
      window.print();
      // Reset print settings after printing
      setPrintSettings(null);
    }
  }, [printSettings]);

  const handlePrint = (settings: PrintProps) => {
    setPrintSettings(settings);
  };

  const value = {
    printSettings,
    handlePrint,
    isPrinting: printSettings !== null,
  };

  return (
    <PrintContext.Provider value={value}>{children}</PrintContext.Provider>
  );
}

export function usePrint() {
  const context = useContext(PrintContext);

  if (context === undefined) {
    throw new Error("usePrint must be used within a PrintProvider");
  }

  return context;
}
